import { type PropsWithChildren } from "react";
import { faShoppingBasket } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { T } from "@repo/transifex";

export const Basket = ({ children }: PropsWithChildren) => {
  return (
    <div className="flex flex-col gap-y-24 bg-white px-16 pb-80 pt-16 text-kiosk-text-primary">
      {children}
    </div>
  );
};

export const BasketHeader = ({ children }: PropsWithChildren) => {
  return (
    <div className="relative isolate flex items-center justify-between">
      <FontAwesomeIcon
        icon={faShoppingBasket}
        fontSize={240}
        aria-hidden
        className="pointer-events-none absolute left-64 top-1/2 -z-10 -translate-y-1/2 rotate-[25deg] text-kiosk-background-secondary"
      />

      <span className="text-left text-8xl font-extrabold">
        <T _str="Basket" />
      </span>

      {children}
    </div>
  );
};

export const BasketBody = ({ children }: PropsWithChildren) => {
  return <div className="flex flex-col">{children}</div>;
};

export const BasketProducts = ({ children }: PropsWithChildren) => {
  return (
    <div className="flex flex-col divide-y-2 divide-kiosk-border-secondary *:py-6 first:*:pt-0">
      {children}
    </div>
  );
};

type BasketSumUpProps = {
  /** Units in the basket, not lines — a line of three counts as three. */
  itemCount: number;
  totalAmount: string;
  /**
   * Only reaches the screen beside an allowance, or beside a deposit shown
   * without `tax` — a breakdown closes the block this row belongs to.
   */
  subtotal?: string;
  allowance?: { applied: string; productCount: number };
  /** One figure for the whole basket — it may aggregate several schemes. */
  deposit?: string;
  tax?: {
    label: string;
    totalExclVat: string;
    lines: { rateLabel: string; taxAmount: string }[];
  };
};

const SumUpRow = ({
  label,
  value,
  valueMuted = false
}: {
  label: React.ReactNode;
  value: string;
  valueMuted?: boolean;
}) => (
  <div className="flex flex-row items-center justify-between text-4xl">
    <span className="flex gap-x-3 font-bold text-kiosk-text-primary">
      {label}
    </span>
    <span
      className={
        valueMuted ? "text-kiosk-text-secondary" : "text-kiosk-text-primary"
      }
    >
      {value}
    </span>
  </div>
);

const RowQualifier = ({ children }: PropsWithChildren) => (
  <span className="font-normal text-kiosk-text-secondary">{children}</span>
);

const SumUpTotalRow = ({
  label,
  value
}: {
  label: React.ReactNode;
  value: string;
}) => (
  <div className="flex flex-row items-center justify-between border-t-2 border-t-kiosk-border-secondary pt-8 text-5xl font-extrabold text-kiosk-text-primary">
    <span className="flex gap-x-3">{label}</span>
    <span>{value}</span>
  </div>
);

const ItemCount = ({ quantity }: { quantity: number }) => {
  if (quantity === 1) return <T _str="{quantity} item" quantity={quantity} />;
  return <T _str="{quantity} items" quantity={quantity} />;
};

const AllowanceAppliedLabel = ({ productCount }: { productCount: number }) => {
  if (productCount <= 0) return <T _str="Allowance applied" />;
  if (productCount === 1) {
    return (
      <T _str="Allowance applied ({count} product)" count={productCount} />
    );
  }
  return <T _str="Allowance applied ({count} products)" count={productCount} />;
};

export const BasketSumUp = ({
  itemCount,
  totalAmount,
  subtotal,
  allowance,
  deposit,
  tax
}: BasketSumUpProps) => {
  const depositRow = deposit ? (
    <SumUpRow label={<T _str="Deposit Fees" />} value={deposit} valueMuted />
  ) : null;

  // Without a breakdown the row sits under the subtotal; `tax` moves it under
  // *Subtotal excl.* instead, so it never reads as part of the breakdown.
  const summaryDeposit = tax ? null : depositRow;

  return (
    <div className="flex flex-col">
      {allowance || summaryDeposit ? (
        <div className="flex flex-col gap-y-4 py-8">
          {subtotal ? (
            <SumUpRow label={<T _str="Subtotal" />} value={subtotal} />
          ) : null}
          {summaryDeposit}
          {allowance ? (
            <SumUpRow
              label={
                <AllowanceAppliedLabel productCount={allowance.productCount} />
              }
              value={allowance.applied}
            />
          ) : null}
        </div>
      ) : null}

      {tax ? (
        <div className="flex flex-col gap-y-4 border-t-2 border-t-kiosk-border-secondary py-8">
          <SumUpRow
            label={
              <>
                <T _str="Subtotal (excl. {vatLabel})" vatLabel={tax.label} />
                <RowQualifier>
                  <ItemCount quantity={itemCount} />
                </RowQualifier>
              </>
            }
            value={tax.totalExclVat}
          />
          {depositRow}
          {tax.lines.map((line) => (
            <SumUpRow
              key={line.rateLabel}
              label={
                <>
                  {tax.label}
                  <RowQualifier>({line.rateLabel})</RowQualifier>
                </>
              }
              value={line.taxAmount}
              valueMuted
            />
          ))}
          <SumUpTotalRow label={<T _str="Total" />} value={totalAmount} />
        </div>
      ) : (
        <SumUpTotalRow
          label={
            <>
              <T _str="Total" />
              <RowQualifier>
                <ItemCount quantity={itemCount} />
              </RowQualifier>
            </>
          }
          value={totalAmount}
        />
      )}
    </div>
  );
};

export const OrderInstructions = ({ children }: PropsWithChildren) => {
  return (
    <div className="flex flex-col gap-y-10">
      <span className="text-5xl font-extrabold">
        <T _str="Order details" />
      </span>

      <div className="flex flex-col gap-y-2">{children}</div>
    </div>
  );
};

export const BasketFooter = ({ children }: { children: React.ReactNode }) => {
  return <div className="fixed inset-x-0 bottom-0">{children}</div>;
};
